import { FC } from 'react';

import { useEffectOnce } from 'usehooks-ts';
import { useTranslation } from 'react-i18next';
import { ModalComponentBaseProps, useModal } from '@pcmp/react-providers';
import { Sheets, ModalHeader, ModalFooter } from '@ingka/modal';
import Avatar from '@ingka/avatar';
import ListView, { ListViewItem } from '@ingka/list-view';
import PersonActiveIcon from '@ingka/ssr-icon/paths/person-active';
import InformationCircleIcon from '@ingka/ssr-icon/paths/information-circle';
import DarkModeIcon from '@ingka/ssr-icon/paths/dark-mode';
import DocumentIcon from '@ingka/ssr-icon/paths/document';

import { useGetV1UsersMeQuery } from '@/redux/apis/frontend-api';
import { useTrackContent } from '@/lib/hooks/tracking';
import { initials } from '@/lib/utils/string';
import LanguageSwitcher from '@/components/helpers/language-switcher';
import { AboutSider } from '../about';
import { ContactSider } from '../contact';
import ChangeThemeSider from '../change-theme';
import { useAppConfig } from '@/redux/features/app-config/hooks';
import { PrivacyPolicySider } from '../privacy-policy';

type UserProfileSiderProps = ModalComponentBaseProps;

type Section = 'about' | 'contact' | 'theme' | 'privacyPolicy';

const SIDER_SECTIONS: Record<Section, FC<ModalComponentBaseProps>> = {
  about: AboutSider,
  contact: ContactSider,
  theme: ChangeThemeSider,
  privacyPolicy: PrivacyPolicySider,
};

const useUserProfileTracking = () => {
  const [trackContent] = useTrackContent();

  useEffectOnce(() => {
    trackContent({ content: 'user-profile', type: 'sider' });
  });
};

const ProfileData = () => {
  const { data: user } = useGetV1UsersMeQuery();

  if (!user) return null;

  return (
    <div className="flex bg-ikea-bg px-6 py-8">
      <Avatar
        data-testid="avatarButton"
        text={initials(user.fullName).slice(0, 2)}
        screenReaderText={user.fullName}
        size="large"
        variant="primary"
      />

      <div className="ml-6">
        <h1
          className="text-skapa-text-1 text-base font-bold leading-normal"
          data-testid="fullName"
        >
          {user.fullName}
        </h1>
        <p
          className="text-skapa-text-3 text-sm font-normal leading-snug"
          data-testid="jobTitle"
        >
          {user.jobTitle}
        </p>
        <p
          className="text-skapa-text-3 text-sm font-normal leading-snug"
          data-testid="cdsLocation"
        >
          {user.cdsLocation}
        </p>
      </div>
    </div>
  );
};

export const UserProfileSider: FC<UserProfileSiderProps> = ({
  handleDismiss,
  ...rest
}) => {
  const { t } = useTranslation();
  const { openModal, closeModal } = useModal();
  const { darkMode } = useAppConfig();
  const goToSection = (section: Section) => async () => {
    await closeModal(0);
    await openModal({
      component: SIDER_SECTIONS[section],
      props: {},
    });
  };

  useUserProfileTracking();

  return (
    <Sheets
      {...rest}
      alignment="right"
      aria-label={t('label.code.user')}
      footer={
        <ModalFooter>
          <LanguageSwitcher />
        </ModalFooter>
      }
      header={<ModalHeader ariaCloseTxt={t('label.close')} />}
      size="small"
    >
      <ProfileData />

      <div className="flex-grow" data-testid="userProfileSider">
        <ListView id="report">
          <ListViewItem
            data-testid="goToAbout"
            size="medium"
            inset
            leadingIcon={InformationCircleIcon}
            emphasised
            title={t('page.about.shortTitle')}
            control="navigational"
            controlIcon="chevronRight"
            onClick={goToSection('about')}
          />

          <ListViewItem
            data-testid="goToContact"
            size="medium"
            inset
            leadingIcon={PersonActiveIcon}
            emphasised
            title={t('page.contact.shortTitle')}
            control="navigational"
            controlIcon="chevronRight"
            onClick={goToSection('contact')}
          />

          <ListViewItem
            data-testid="goToPrivacyPolicy"
            size="medium"
            inset
            leadingIcon={DocumentIcon}
            emphasised
            title={t('page.privacyPolicy.title')}
            control="navigational"
            controlIcon="chevronRight"
            onClick={goToSection('privacyPolicy')}
          />

          <ListViewItem
            data-testid="themeBtn"
            size="medium"
            inset
            leadingIcon={DarkModeIcon}
            emphasised
            title={t('label.changeTheme.title')}
            description={t(
              `label.changeTheme.${darkMode ? 'darkMode' : 'lightMode'}`
            )}
            control="navigational"
            controlIcon="chevronRight"
            onClick={goToSection('theme')}
          />
        </ListView>
      </div>
    </Sheets>
  );
};
